import {Scene} from "../scene/Scene";
import {ClosingContainer} from "../utils/ui/ClosingContainer";
import {Field} from "../utils/ui/Field";
import {DropDownInput} from "../utils/ui/DropDownInput";
import {AddonManager} from "../scene/addon/AddonManager";
import {SceneNodeState} from "../scene/ISceneNode";
import {ISceneAddon} from "../scene/addon/ISceneAddon";
import {Templating} from "../utils/Templating";
import {Main} from "../Main";
import {Vector3} from "three";
import {WallAddon} from "../scene/addon/WallAddon";
import {BooleanInput} from "../utils/ui/BooleanInput";
import {MaterialManager} from "../material/MaterialManager";
import {WallSide} from "building/Building";


export class ScenePanel extends ClosingContainer
{
	private _scene: Scene;
	private _sceneNodes: {
		[id in WallSide]: HTMLDivElement;
	};
	private _addNewSelect: {
		[id in WallSide]: DropDownInput<string>;
	};
	private _cancelBtn: HTMLElement;
	private _colorDropDown: Field;

	private _savedCameraPosition: Vector3;

	constructor(scene: Scene, orderId: number, colorDropDown: Field)
	{
		super({scriptId: "scenePanel", label: "scenePanel"});

		this._sceneNodes = {
			front: null,
			right: null,
			back: null,
			left: null
		};
		this._addNewSelect = {
			front: null,
			right: null,
			back: null,
			left: null
		};

		const nextStepDiv = this.element.querySelector('[data-type="nextStep"]');
		nextStepDiv.setAttribute("data-target", (orderId + 1).toString());

		const transparentWalls = new BooleanInput({value: false, label: "Transparent Walls: ", addLabel: true});
		nextStepDiv.parentElement.insertBefore(transparentWalls.element, nextStepDiv);
		transparentWalls.changeSignal.add((value: boolean) =>
		{
			MaterialManager.getInstance().setWallTransparency(value);
		});

		this._scene = scene;
		this._scene.changeSignal.add(this.onSceneChange);
		this._colorDropDown = colorDropDown;
		this.init();
		this.element.setAttribute("data-orderId", orderId.toString());
	}

	private init()
	{
		for (const key in this._addNewSelect)
		{
			this._addNewSelect[key] = this.createAddNewSelect(key as WallSide);
			const addNewSelect = this._addNewSelect[key];
			addNewSelect.userData.wall = key;
			addNewSelect.disable();
			addNewSelect.changeSignal.add(this.onAddonSelected);

			const sideLabel = document.createElement("div");
			sideLabel.classList.add("sideLabel");
			sideLabel.textContent = `${key}:`;
			(addNewSelect.element as HTMLDivElement).insertBefore(sideLabel, addNewSelect.element.firstChild);

			this.element.querySelector(".scene-header").appendChild(addNewSelect.element);

			this._sceneNodes[key] = addNewSelect.element.querySelector(`*[id="sceneNodes"]`);
		}

		this._cancelBtn = <HTMLDivElement>this.element.querySelector(`*[data-id="cancel"]`);
		this._cancelBtn.addEventListener("click", this.onCancelPlacing);


		// add initial nodes

		const addons = this._scene.addons;

		for (const addon of addons)
		{
			this.addNodeDom(addon);
		}

		this.onAddonsLoaded();
	}

	private setCamera(side: WallSide)
	{
		this._savedCameraPosition = new Vector3().copy(Main.getInstance().content.camera.position);
		Main.getInstance().content.controller.onViewPresetChange(side);
	}

	private resetCameraToSavedPosition()
	{
		Main.getInstance().content.controller.orbitControls.setPosition(this._savedCameraPosition.x, this._savedCameraPosition.y, this._savedCameraPosition.z);
	}

	private onAddonsLoaded = () =>
	{
		for (const key in this._addNewSelect)
		{
			this._addNewSelect[key].enable();
		}
	};

	private onAddonSelected = (addonId: string, dropDownInput: DropDownInput<string>) =>
	{
		// start placing the node
		const side = dropDownInput.userData.wall;
		const addon = AddonManager.getInstance().createAddon(addonId, side);
		if (addon == null)
		{
			return;
		}

		this.setCamera(dropDownInput.id as WallSide);

		this._scene.startPlacing(addon);
	};

	private onKeyPress = (e: KeyboardEvent) =>
	{
		if (e.keyCode === 27)
		{
			// Esc pressed
			this.cancelPlacing();
		}
	};

	private onCancelPlacing = () =>
	{
		this.cancelPlacing();
	};

	public cancelPlacing()
	{
		this.resetCameraToSavedPosition();
		this._scene.cancelPlacing();
	}

	private onSceneChange = (state: SceneNodeState, node: ISceneAddon) =>
	{

		if (state === SceneNodeState.PLACING)
		{
			this.element.classList.add("placing");
			window.document.body.classList.add("copy");

			// listen for Esc key press
			window.document.addEventListener("keyup", this.onKeyPress);

			if (node.added)
			{
				// indicate which node is currently being moved
				var sceneNode = <HTMLElement>this.element.querySelector('[data-nodeid="' + node.id + '"]');
				sceneNode.classList.add("active");
			}
		}

		if (state === SceneNodeState.PLACED || state === SceneNodeState.CANCELED)
		{
			this.element.classList.remove("placing");
			window.document.body.classList.remove("copy");

			window.document.removeEventListener("keyup", this.onKeyPress);

			// end of a move

			// remove indication

			if (node.added)
			{
				// remove indication
				const sceneNode = <HTMLElement>this.element.querySelector(`[data-nodeid=${node.id}]`);
				sceneNode.classList.remove("active");
			}
		}

		if (state === SceneNodeState.PLACED)
		{
			if (!node.added)
			{
				// not yet added (it's not a move but the first placement)
				this.addNodeDom(node);
			}
		}

		if (state === SceneNodeState.DESTROYED)
		{
			const side = node.getPosition().wall as WallSide;
			const nodeEl = this._sceneNodes[side].querySelector(`[data-nodeid=${node.id}]`);
			if (nodeEl)
			{
				this._sceneNodes[side].removeChild(nodeEl);
			}
		}
	};

	private createAddNewSelect(key: WallSide)
	{
		var select = new DropDownInput({
			tpl: {
				element: document.getElementById("addon-element").textContent,
				button: document.getElementById("addon-button").textContent,
				list: document.getElementById("addon-list").textContent,
				option: document.getElementById("addon-option").textContent
			},
			values: AddonManager.getInstance().getAddonTypes(),
			idProperty: "id",
			id: key
		});

		return select;
	}

	private addNodeDom(node: ISceneAddon)
	{
		const nodeEl = Templating.renderDOMFromScript("sceneNode", {name: node.name, id: node.id});

		const deleteIcon = nodeEl.querySelector("[data-deleteNode]");
		deleteIcon.addEventListener("click", this.onDeleteNode);

		const moveIcon = nodeEl.querySelector("[data-moveNode]");
		moveIcon.addEventListener("click", this.onMoveNode);

		const sceneNodeName = nodeEl.querySelector(".sceneNodeName");
		sceneNodeName.addEventListener("click", this.highlightNode);

		const side = node.getPosition().wall as WallSide;
		this._sceneNodes[side].appendChild(nodeEl);
	}

	private onDeleteNode = (event: MouseEvent) =>
	{
		const node = this.findNode(event);
		this._scene.destroyAddon(node);
	};

	private onMoveNode = (event: MouseEvent) =>
	{
		const node = this.findNode(event);
		const side = node.getPosition().wall;
		this.setCamera(side);
		this._scene.startPlacing(node);
	};

	private highlightNode = (event: MouseEvent) =>
	{
		const node = this.findNode(event);
		this._scene.highlightNode(node as WallAddon);
	};

	private findNode(event: MouseEvent)
	{
		var target = <HTMLElement>event.currentTarget;
		var nodeId = target.getAttribute("data-nodeId");
		return this._scene.findAddonById(nodeId);
	}
}